import React from 'react';
import block from 'bem-cn-lite';
import {compose} from 'recompose';
import _ from 'lodash';

import {Icon, Tooltip, TooltipProps} from '@yandex-data-ui/common';

import withRouter, {WithRouterProps} from 'hoc/withRouter';
import withLocale, {WithLocaleProps} from 'hoc/withLocale';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import {Locale} from 'contexts/LocaleContext';
import metrika from 'counters/metrika';
import {importContext} from 'utils';
import {getLocales} from '../../../../../common/i18n/locales';
import {getAbsoluteLocaleUrl} from '../../../../../common/i18n/utils';
import ButtonWithIcon from 'components/Buttons/ButtonWithIcon/ButtonWithIcon';

import languageIcon from 'icons/language.svg';
import tickIcon from 'icons/tick.svg';

import './LangSwitch.scss';

const b = block('lang-switch');

const flags = importContext<SVGIconData>(require.context('icons/flags/', false, /\.svg$/));

export interface LangSwitchOwnProps {
    className?: string;
    tooltipClassName?: string;
    type?: 'icon' | 'text';
    direction?: TooltipProps['placement'];
    metrikaGoal?: string;
}

export type LangSwitchProps = LangSwitchOwnProps &
    WithLocaleProps &
    WithRouterProps &
    WithTranslationProps;

class LangSwitch extends React.Component<LangSwitchProps> {
    tooltipRef: Tooltip | null = null;

    componentDidMount() {
        window.addEventListener('scroll', this.onCloseLanguage);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.onCloseLanguage);
    }

    render() {
        const {className, type = 'text', direction = 'bottom', tooltipClassName} = this.props;

        return (
            <div className={b(null, className)}>
                <Tooltip
                    ref={(ref) => {
                        this.tooltipRef = ref;
                    }}
                    openOnHover={false}
                    hasArrow={false}
                    placement={direction}
                    content={this.renderTooltipContent()}
                    tooltipOffset={[-29, 14]}
                    tooltipClassName={b('tooltip', tooltipClassName)}
                >
                    {type === 'icon' ? this.renderIcon() : this.renderText()}
                </Tooltip>
            </div>
        );
    }

    private renderIcon() {
        return (
            <ButtonWithIcon className={b('button')} icon={languageIcon} iconSize={24} size="xs" />
        );
    }

    private renderText() {
        const {i18nK} = this.props;

        return (
            <span className={b('text')}>
                <Icon className={b('text-icon')} size={16} data={languageIcon} />{' '}
                {i18nK('locale-button')}
            </span>
        );
    }

    private renderTooltipContent() {
        const locales = (getLocales() as Locale[]) ?? [];

        return (
            <div className={b('tooltip-content')}>
                {locales.map((locale) => this.renderLocaleItem(locale))}
            </div>
        );
    }

    private renderLocaleItem(locale: Locale) {
        const {locale: currentLocale} = this.props;
        const isCurrentLocale = currentLocale.code === locale.code;
        const flagIcon = flags[locale.region] || flags['int'];

        return (
            <a
                key={locale.code}
                className={b('tooltip-locale')}
                href={isCurrentLocale ? undefined : this.getLinkUrl(locale)}
                onClick={this.onChangeLanguage}
            >
                <div className={b('tooltip-locale-line')}>
                    <Icon
                        className={b('tooltip-locale-flag')}
                        data={flagIcon}
                        width={18}
                        height={12}
                    />
                    <div className={b('tooltip-lang-text')}>
                        <span className={b('tooltip-locale-region')}>{locale.regionName}</span>
                    </div>
                    {isCurrentLocale && (
                        <div className={b('tooltip-locale-tick')}>
                            <Icon data={tickIcon} height={12} />
                        </div>
                    )}
                </div>
                <div className={b('tooltip-locale-line')}>
                    <span className={b('tooltip-locale-lang')}>{locale.langName}</span>
                </div>
            </a>
        );
    }

    private onCloseLanguage = () => {
        this.tooltipRef?.closeTooltip();
    };

    private onChangeLanguage = () => {
        const {metrikaGoal} = this.props;

        this.onCloseLanguage();

        const goal = metrika.goalSwitchLang(_.upperCase(metrikaGoal));
        metrika.reachGoal('main', goal);
    };

    private getLinkUrl(locale: Locale) {
        const {
            router: {hostname, as},
        } = this.props;

        return getAbsoluteLocaleUrl(locale, hostname, as);
    }
}

export default compose<LangSwitchProps, LangSwitchOwnProps>(
    withLocale,
    withRouter,
    withTranslation('common'),
)(LangSwitch);
